import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Link,
  Chip,
  Alert,
  TextField,
} from '@mui/material';
import TimezoneUtils from '../utils/timezoneUtils';
import {
  resolveDashboardChartDateRange,
  fillDailyCostSeries,
  sumDailyCostSeries,
  formatDashboardRangeLabel,
  DASHBOARD_DATE_RANGE_OPTIONS,
  COST_CHART_SERIES,
} from '../utils/dashboardChartData';

const CATEGORY_LABELS = {
  fill_form: 'Fill Form',
  resume_customization: 'Resume Customization',
  email: 'Email',
  mongtro: 'Mongtro',
  others: 'Others',
  company_research: 'Company Research',
  job_search: 'Job Search',
};

const formatUsd = (value) => `$${Number(value || 0).toFixed(4)}`;

const getDateRangeParams = (days, fromDate = '') => {
  const { startDate, endDate } = resolveDashboardChartDateRange({ days, fromDate });
  return {
    startDate: TimezoneUtils.formatForAPI(startDate),
    endDate: TimezoneUtils.formatForAPI(endDate),
  };
};

const needsSelectedDate = (dateRange) => dateRange === 'custom' || dateRange === 'single';

const CostsDashboard = ({ dateRange: dateRangeProp, fromDate: fromDateProp, rangeLabel }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedOperation, setSelectedOperation] = useState('all');
  const [selectedModel, setSelectedModel] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ operations: [], models: [] });
  const [internalDateRange, setInternalDateRange] = useState('14');
  const [internalFromDate, setInternalFromDate] = useState('');
  const [summary, setSummary] = useState(null);
  const [operations, setOperations] = useState([]);
  const [searchRuns, setSearchRuns] = useState([]);
  const [activeTab, setActiveTab] = useState('openai');
  const [operationsTotalCost, setOperationsTotalCost] = useState(0);
  const [operationsDisplayedCost, setOperationsDisplayedCost] = useState(0);
  const [operationsTotalCount, setOperationsTotalCount] = useState(0);
  const [searchRunsTotalCost, setSearchRunsTotalCost] = useState(0);
  const [searchRunsOpenaiCost, setSearchRunsOpenaiCost] = useState(0);
  const [searchRunsScrapingdogCost, setSearchRunsScrapingdogCost] = useState(0);
  const [searchRunsDisplayedCost, setSearchRunsDisplayedCost] = useState(0);
  const [searchRunsTotalCount, setSearchRunsTotalCount] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [loadingSearchRuns, setLoadingSearchRuns] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const isStandalone = dateRangeProp == null;
  const dateRange = dateRangeProp ?? internalDateRange;
  const fromDate = dateRangeProp != null ? (fromDateProp ?? '') : internalFromDate;
  const selectedRangeLabel = rangeLabel || formatDashboardRangeLabel({ days: dateRange, fromDate });

  const dateParams = useMemo(
    () => getDateRangeParams(dateRange, fromDate),
    [dateRange, fromDate],
  );

  const isDateRangeReady = !needsSelectedDate(dateRange) || Boolean(fromDate);

  const openAiFilterParams = useMemo(
    () => ({
      userId: selectedUser,
      operation: selectedOperation,
      model: selectedModel,
      service: 'openai',
      excludeCategory: 'job_search',
      ...dateParams,
    }),
    [selectedUser, selectedOperation, selectedModel, dateParams],
  );

  useEffect(() => {
    setSelectedOperation('all');
    setSelectedModel('all');
  }, [selectedUser, dateParams, activeTab]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cost-analytics/users`);
        setUsers(response.data || []);
      } catch (error) {
        console.error('Failed to fetch cost users:', error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!isDateRangeReady) {
      setSummary(null);
      return undefined;
    }

    const fetchSummary = async () => {
      setLoadingSummary(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cost-analytics/summary`, {
          params: dateParams,
        });
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to fetch cost summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchSummary();
  }, [dateParams, isDateRangeReady]);

  useEffect(() => {
    if (!isDateRangeReady || activeTab !== 'openai') {
      setFilterOptions({ operations: [], models: [] });
      return undefined;
    }

    const fetchFilters = async () => {
      setLoadingFilters(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cost-analytics/filters`, {
          params: {
            userId: selectedUser,
            service: 'openai',
            excludeCategory: 'job_search',
            ...dateParams,
          },
        });
        setFilterOptions({
          operations: response.data?.operations || [],
          models: response.data?.models || [],
        });
      } catch (error) {
        console.error('Failed to fetch cost filters:', error);
        setFilterOptions({ operations: [], models: [] });
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchFilters();
  }, [selectedUser, dateParams, activeTab, isDateRangeReady]);

  useEffect(() => {
    if (!isDateRangeReady || activeTab !== 'openai') return undefined;

    const fetchOperations = async () => {
      setLoadingOperations(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cost-analytics/operations`, {
          params: {
            ...openAiFilterParams,
            limit: 100,
          },
        });
        setOperations(response.data.items || []);
        setOperationsTotalCost(response.data.totalCostUsd || 0);
        setOperationsDisplayedCost(response.data.displayedCostUsd || 0);
        setOperationsTotalCount(response.data.total || 0);
      } catch (error) {
        console.error('Failed to fetch operation costs:', error);
      } finally {
        setLoadingOperations(false);
      }
    };
    fetchOperations();
  }, [activeTab, openAiFilterParams, isDateRangeReady]);

  useEffect(() => {
    if (!isDateRangeReady || activeTab !== 'job_search') return undefined;

    const fetchSearchRuns = async () => {
      setLoadingSearchRuns(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cost-analytics/search-runs`, {
          params: {
            userId: selectedUser,
            limit: 50,
            ...dateParams,
          },
        });
        setSearchRuns(response.data.runs || []);
        setSearchRunsTotalCost(response.data.totalCostUsd || 0);
        setSearchRunsOpenaiCost(response.data.totalOpenaiUsd || 0);
        setSearchRunsScrapingdogCost(response.data.totalScrapingdogUsd || 0);
        setSearchRunsDisplayedCost(response.data.displayedCostUsd || 0);
        setSearchRunsTotalCount(response.data.total || 0);
      } catch (error) {
        console.error('Failed to fetch search run costs:', error);
      } finally {
        setLoadingSearchRuns(false);
      }
    };
    fetchSearchRuns();
  }, [activeTab, selectedUser, dateParams, isDateRangeReady]);

  const dailySeries = useMemo(
    () => fillDailyCostSeries(summary?.daily, dateRange, fromDate),
    [summary, dateRange, fromDate],
  );

  const chartTotals = useMemo(
    () => sumDailyCostSeries(dailySeries),
    [dailySeries],
  );

  const hasOpenAiFilters = selectedUser !== 'all'
    || selectedOperation !== 'all'
    || selectedModel !== 'all';

  const chartData = useMemo(() => ({
    labels: dailySeries.map((entry) => entry._id),
    datasets: COST_CHART_SERIES.map((series) => ({
      label: series.label,
      data: dailySeries.map((entry) => entry[series.key] || 0),
      borderColor: series.borderColor,
      backgroundColor: series.backgroundColor,
      tension: 0.25,
      fill: Boolean(series.fill),
      borderWidth: series.key === 'totalUsd' ? 2.5 : 2,
    })),
  }), [dailySeries]);

  const visibleSearchRunTotals = useMemo(
    () => searchRuns.reduce(
      (acc, run) => ({
        openaiUsd: acc.openaiUsd + (run.costs?.openaiUsd || 0),
        scrapingdogUsd: acc.scrapingdogUsd + (run.costs?.scrapingdogUsd || 0),
        totalUsd: acc.totalUsd + (run.costs?.totalUsd || 0),
      }),
      { openaiUsd: 0, scrapingdogUsd: 0, totalUsd: 0 },
    ),
    [searchRuns],
  );

  const handleStandaloneDateRangeChange = (value) => {
    setInternalDateRange(value);
    if (!needsSelectedDate(value)) {
      setInternalFromDate('');
    }
  };

  return (
    <Box>
      {isStandalone && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={internalDateRange}
              label="Date Range"
              onChange={(e) => handleStandaloneDateRangeChange(e.target.value)}
            >
              {DASHBOARD_DATE_RANGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {needsSelectedDate(internalDateRange) && (
            <TextField
              label={internalDateRange === 'single' ? 'Date' : 'From Date'}
              type="date"
              size="small"
              value={internalFromDate}
              onChange={(e) => setInternalFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}
        </Box>
      )}

      {!isDateRangeReady && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Select a date to view costs for the selected range.
        </Alert>
      )}

      {isDateRangeReady && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">Cost Over Time</Typography>
            <Typography variant="body2" color="text.secondary">
              All users · {selectedRangeLabel}
            </Typography>
          </Box>
          {!loadingSummary && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {COST_CHART_SERIES.map((series) => (
                <Paper key={series.key} sx={{ px: 2, py: 1, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">{series.label}</Typography>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: series.borderColor }}>
                    {formatUsd(chartTotals[series.key])}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
          {loadingSummary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 360 }}>
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false,
                  },
                  plugins: {
                    legend: { position: 'top' },
                    tooltip: {
                      callbacks: {
                        label: (context) => `${context.dataset.label}: $${Number(context.raw || 0).toFixed(4)}`,
                      },
                    },
                  },
                  scales: {
                    y: {
                      min: 0,
                      ticks: {
                        callback: (value) => `$${value}`,
                      },
                    },
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="openai" label="OpenAI" />
          <Tab value="job_search" label="Job Search" />
        </Tabs>

        {activeTab === 'openai' && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>User</InputLabel>
                <Select
                  value={selectedUser}
                  label="User"
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={!isDateRangeReady}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Operation</InputLabel>
                <Select
                  value={selectedOperation}
                  label="Operation"
                  onChange={(e) => setSelectedOperation(e.target.value)}
                  disabled={!isDateRangeReady || loadingFilters}
                >
                  <MenuItem value="all">All Operations</MenuItem>
                  {filterOptions.operations.map((operation) => (
                    <MenuItem key={operation} value={operation}>
                      {operation}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Model</InputLabel>
                <Select
                  value={selectedModel}
                  label="Model"
                  onChange={(e) => setSelectedModel(e.target.value)}
                  disabled={!isDateRangeReady || loadingFilters}
                >
                  <MenuItem value="all">All Models</MenuItem>
                  {filterOptions.models.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!isDateRangeReady ? null : loadingOperations ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper sx={{ px: 2, py: 1.5, mb: 2, bgcolor: 'action.hover', display: 'inline-block' }}>
                  <Typography variant="body2" color="text.secondary">
                    {hasOpenAiFilters ? 'Filtered OpenAI total' : 'Platform OpenAI total'} ({selectedRangeLabel})
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatUsd(operationsTotalCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {operationsTotalCount} matching operation{operationsTotalCount === 1 ? '' : 's'}
                    {!hasOpenAiFilters && (
                      <> · excludes Job Search (see chart / Job Search tab)</>
                    )}
                  </Typography>
                  {operationsTotalCount > operations.length && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Table shows {operations.length} of {operationsTotalCount} · visible rows {formatUsd(operationsDisplayedCost)}
                    </Typography>
                  )}
                </Paper>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Operation</TableCell>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell>Bid Link</TableCell>
                        <TableCell align="right">Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operations.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {TimezoneUtils.formatForDisplay(new Date(item.timestamp), {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell>{item.userName}</TableCell>
                          <TableCell>
                            {CATEGORY_LABELS[item.operationCategory] || item.operationCategory}
                          </TableCell>
                          <TableCell>{item.operation}</TableCell>
                          <TableCell>{item.model || '—'}</TableCell>
                          <TableCell align="right">{item.totalTokens || 0}</TableCell>
                          <TableCell sx={{ maxWidth: 240 }}>
                            {item.bidLinkUrl ? (
                              <Link href={item.bidLinkUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                {item.bidLinkUrl}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell align="right">{formatUsd(item.costUsd)}</TableCell>
                        </TableRow>
                      ))}
                      {operations.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No OpenAI operations found for this filter
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}

        {activeTab === 'job_search' && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>User</InputLabel>
                <Select
                  value={selectedUser}
                  label="User"
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={!isDateRangeReady}
                >
                  <MenuItem value="all">All Users</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {!isDateRangeReady ? null : loadingSearchRuns ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Paper sx={{ px: 2, py: 1.5, mb: 2, bgcolor: 'action.hover', display: 'inline-block' }}>
                  <Typography variant="body2" color="text.secondary">
                    Job Search total ({selectedRangeLabel})
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatUsd(searchRunsTotalCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    OpenAI {formatUsd(searchRunsOpenaiCost)} · ScrapingDog {formatUsd(searchRunsScrapingdogCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {searchRunsTotalCount} search run{searchRunsTotalCount === 1 ? '' : 's'}
                    {searchRunsTotalCount > searchRuns.length && (
                      <> · table shows {searchRuns.length} of {searchRunsTotalCount}</>
                    )}
                  </Typography>
                </Paper>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Started</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Queries</TableCell>
                        <TableCell align="right">Jobs</TableCell>
                        <TableCell align="right">OpenAI</TableCell>
                        <TableCell align="right">ScrapingDog</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchRuns.map((run) => (
                        <TableRow key={run.id}>
                          <TableCell>
                            {TimezoneUtils.formatForDisplay(new Date(run.startedAt), {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                          <TableCell>{run.userName}</TableCell>
                          <TableCell>{run.source}</TableCell>
                          <TableCell>
                            <Chip size="small" label={run.status} />
                          </TableCell>
                          <TableCell align="right">{run.totalQueries}</TableCell>
                          <TableCell align="right">{run.totalJobsFound}</TableCell>
                          <TableCell align="right">{formatUsd(run.costs?.openaiUsd)}</TableCell>
                          <TableCell align="right">{formatUsd(run.costs?.scrapingdogUsd)}</TableCell>
                          <TableCell align="right">{formatUsd(run.costs?.totalUsd)}</TableCell>
                        </TableRow>
                      ))}
                      {searchRuns.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No search runs found for this filter
                          </TableCell>
                        </TableRow>
                      )}
                      {searchRuns.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="right">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {searchRunsTotalCount > searchRuns.length ? 'Table subtotal' : 'Total'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatUsd(visibleSearchRunTotals.openaiUsd)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatUsd(visibleSearchRunTotals.scrapingdogUsd)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {formatUsd(visibleSearchRunTotals.totalUsd)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CostsDashboard;
